import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit2, ChevronDown, ChevronUp, Bot, Gamepad2 } from "lucide-react";
import { WebhookManagement } from "@/components/webhooks/WebhookManagement";

interface Question {
  id: string;
  question: string;
  category: string;
  difficulty: string;
  is_active: boolean;
  created_at: string;
  type: 'exam' | 'game';
  game_type?: string;
}

interface NewQuestion {
  question: string;
  category: string;
  difficulty: string;
  is_active: boolean;
  type: 'exam' | 'game';
  game_type?: string;
}

export const ManageQuestions = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState<NewQuestion>({
    question: "",
    category: "",
    difficulty: "easy",
    is_active: true,
    type: "exam",
    game_type: undefined
  });
  const { toast } = useToast();

  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!data) {
        setQuestions([]);
        return;
      }

      // Convert Supabase data to Question type
      const formattedQuestions: Question[] = data.map((q: any) => ({
        id: q.id,
        question: q.question,
        category: q.category,
        difficulty: q.difficulty,
        is_active: Boolean(q.is_active),
        created_at: q.created_at,
        type: q.type as 'exam' | 'game',
        game_type: q.game_type
      }));

      setQuestions(formattedQuestions);
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch questions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    try {
      const { error } = await supabase
        .from('questions')
        .insert([{
          question: newQuestion.question,
          category: newQuestion.category,
          difficulty: newQuestion.difficulty,
          is_active: newQuestion.is_active,
          type: newQuestion.type,
          game_type: newQuestion.game_type
        }]);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Question added successfully",
      });
      
      setShowAddForm(false);
      setNewQuestion({ 
        question: "", 
        category: "", 
        difficulty: "easy",
        is_active: true,
        type: "exam",
        game_type: undefined
      });
      fetchQuestions();
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to add question",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('questions')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: `Question status updated to ${!isActive ? "active" : "inactive"}`,
      });
      fetchQuestions();
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to update question status",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Question Management</CardTitle>
              <CardDescription>
                Manage all questions, exams, and games
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative w-64">
                <WebhookManagement />
              </div>
              <div className="relative w-64">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Games Editor</CardTitle>
                    <CardDescription className="text-xs">
                      Coming soon - manage game questions and settings
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
              <Button onClick={() => setShowAddForm(!showAddForm)}>
                <Plus className="mr-2 h-4 w-4" />
                {showAddForm ? "Cancel" : "Add New Question"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showAddForm && (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Question</label>
              <Input
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                placeholder="Enter your question here..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Input
                value={newQuestion.category}
                onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                placeholder="Enter category..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <select
                value={newQuestion.type}
                onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value as NewQuestion['type'] })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="exam">Exam Question</option>
                <option value="game">Game Question</option>
              </select>
            </div>
            {newQuestion.type === 'game' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Game Type</label>
                <select
                  value={newQuestion.game_type || ''}
                  onChange={(e) => setNewQuestion({ ...newQuestion, game_type: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select game type...</option>
                  <option value="memory">Memory Game</option>
                  <option value="word-search">Word Search</option>
                  <option value="twenty-four">24 Game</option>
                </select>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Difficulty</label>
              <select
                value={newQuestion.difficulty}
                onChange={(e) => setNewQuestion({ ...newQuestion, difficulty: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Active</label>
              <input
                type="checkbox"
                checked={newQuestion.is_active}
                onChange={(e) => setNewQuestion({ ...newQuestion, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>
            <Button onClick={handleAddQuestion} className="w-full">
              Add Question
            </Button>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Questions</CardTitle>
          <CardDescription>
            List of all questions and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Question</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Difficulty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map((question) => (
                <TableRow key={question.id}>
                  <TableCell>{question.question}</TableCell>
                  <TableCell>{question.category}</TableCell>
                  <TableCell>
                    {question.type === 'exam' ? 'Exam' : 'Game'}
                    {question.type === 'game' && question.game_type && (
                      <span className="ml-2 text-sm text-gray-500">({question.game_type})</span>
                    )}
                  </TableCell>
                  <TableCell>{question.difficulty}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      question.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {question.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell>{new Date(question.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(question.id, question.is_active)}
                      >
                        <span className="sr-only">Toggle active status</span>
                        {question.is_active ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
